import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowUpRight, ArrowDownRight, Loader2, Check, Circle, Copy, CheckCircle2 } from 'lucide-react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
  HTMLAttributes,
  TableHTMLAttributes,
  ThHTMLAttributes,
  TdHTMLAttributes,
} from 'react';

/* ---------------------------------------------------------------------------
 * Button
 * Mirrors admin button.tsx. `gradient` kept as partner signature for hero CTAs.
 * -------------------------------------------------------------------------*/
export function Button({
  className,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'gradient';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
}) {
  return (
    <button
      {...rest}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium select-none',
        'transition-[background,box-shadow,transform,color,border] duration-[var(--duration-base)] ease-[var(--ease-out)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
        'disabled:opacity-50 disabled:pointer-events-none active:scale-[0.98]',
        size === 'xs' && 'h-7 px-2.5 text-xs rounded-[var(--radius-xs)]',
        size === 'sm' && 'h-8 px-3 text-[13px] rounded-[var(--radius-sm)]',
        size === 'md' && 'h-9 px-4 text-sm rounded-[var(--radius-sm)]',
        size === 'lg' && 'h-11 px-5 text-[15px] rounded-[var(--radius-md)]',
        size === 'icon' && 'h-8 w-8 rounded-[var(--radius-sm)]',
        variant === 'primary' &&
          'bg-[var(--accent)] text-[var(--accent-fg)] shadow-[0_1px_0_oklch(1_0_0/0.18)_inset,0_8px_28px_-8px_var(--accent-glow)] hover:bg-[var(--accent-bright)]',
        variant === 'gradient' && 'btn-gradient text-[var(--accent-fg)]',
        variant === 'secondary' &&
          'bg-[var(--bg-elevated)] text-[var(--text)] border border-[var(--border-strong)] hover:bg-[var(--bg-raised)] hover:border-[color:var(--accent-soft)]',
        variant === 'ghost' &&
          'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)]',
        variant === 'outline' &&
          'bg-transparent text-[var(--text)] border border-[var(--border-strong)] hover:bg-[var(--bg-elevated)]',
        variant === 'danger' && 'bg-[var(--danger)] text-white hover:opacity-90',
        className,
      )}
    >
      {loading ? <Loader2 size={14} className="animate-spin" aria-label="Загрузка" /> : null}
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------------------
 * Inputs
 * -------------------------------------------------------------------------*/
export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={clsx(
        'w-full h-10 px-3 rounded-[var(--radius-sm)] bg-[var(--bg-base)] border border-[var(--border-strong)] text-[var(--text)]',
        'placeholder:text-[var(--text-quiet)] outline-none motion-base',
        'focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-soft)]',
        className,
      )}
    />
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      className={clsx(
        'w-full min-h-24 p-3 rounded-[var(--radius-sm)] bg-[var(--bg-base)] border border-[var(--border-strong)] text-[var(--text)]',
        'placeholder:text-[var(--text-quiet)] outline-none motion-base resize-y',
        'focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-soft)]',
        className,
      )}
    />
  );
}

export function Select({ className, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...rest}
      className={clsx(
        'w-full h-10 px-3 rounded-[var(--radius-sm)] bg-[var(--bg-base)] border border-[var(--border-strong)] text-[var(--text)]',
        'outline-none motion-base appearance-none bg-no-repeat pr-9',
        'focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-soft)]',
        className,
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundPosition: 'right 12px center',
        backgroundSize: '14px',
      }}
    />
  );
}

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[10px] uppercase tracking-[0.14em] font-semibold text-[var(--text-muted)] mb-1.5"
    >
      {children}
    </label>
  );
}

export function HelperText({ children }: { children: ReactNode }) {
  return <p className="text-xs text-[var(--text-muted)] mt-1.5">{children}</p>;
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-[var(--danger)] mt-1.5 flex items-center gap-1">{message}</p>;
}

/* ---------------------------------------------------------------------------
 * Card + subcomponents (admin parity)
 * -------------------------------------------------------------------------*/
export function Card({
  className,
  children,
  interactive,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      {...rest}
      className={clsx(
        'rounded-[var(--radius-lg)] bg-[var(--bg-raised)] border border-[var(--border)] shadow-[var(--shadow-sm)]',
        'transition-[border,box-shadow,transform] duration-[var(--duration-base)] ease-[var(--ease-out)]',
        interactive &&
          'hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-md)] hover:-translate-y-px cursor-pointer',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={clsx(
        'flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[var(--border-subtle)] min-h-[48px]',
        className,
      )}
    />
  );
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      {...rest}
      className={clsx(
        'text-[14px] font-semibold text-[var(--text)] tracking-tight leading-tight m-0',
        className,
      )}
    />
  );
}

export function CardContent({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={clsx('p-5', className)} />;
}

/* ---------------------------------------------------------------------------
 * Badge (no border)
 * -------------------------------------------------------------------------*/
export function Badge({
  children,
  tone = 'neutral',
  dot,
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';
  dot?: boolean;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[var(--radius-pill)] text-[11px] font-medium uppercase tracking-wide whitespace-nowrap',
        tone === 'neutral' && 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]',
        tone === 'success' && 'bg-[var(--success-soft)] text-[var(--success)]',
        tone === 'warning' && 'bg-[var(--warning-soft)] text-[var(--warning)]',
        tone === 'danger' && 'bg-[var(--danger-soft)] text-[var(--danger)]',
        tone === 'info' && 'bg-[var(--info-soft)] text-[var(--info)]',
        tone === 'accent' && 'bg-[var(--accent-soft)] text-[var(--accent-bright)]',
        className,
      )}
    >
      {dot && (
        <span
          className={clsx(
            'inline-block w-1.5 h-1.5 rounded-full',
            tone === 'success' && 'bg-[var(--success)]',
            tone === 'warning' && 'bg-[var(--warning)]',
            tone === 'danger' && 'bg-[var(--danger)]',
            tone === 'info' && 'bg-[var(--info)]',
            tone === 'accent' && 'bg-[var(--accent-bright)]',
            tone === 'neutral' && 'bg-[var(--text-muted)]',
          )}
        />
      )}
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * PageHeader — no icon-box. `icon` accepted but ignored for back-compat.
 * -------------------------------------------------------------------------*/
export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  actions?: ReactNode;
  /** @deprecated leading icon-box removed to match admin */
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[var(--text-muted)] mb-1.5">
            {eyebrow}
          </div>
        )}
        <h1 className="text-[28px] md:text-[34px] font-semibold tracking-tight text-[var(--text)] leading-[1.1]">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-[var(--text-muted)] mt-1.5">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Modal — portal-based, ESC + backdrop close, footer slot, sheet variant.
 * -------------------------------------------------------------------------*/
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  variant = 'dialog',
  width,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  variant?: 'dialog' | 'sheet';
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  const isSheet = variant === 'sheet';
  const w = width ?? (isSheet ? '480px' : '520px');

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex"
      style={{
        alignItems: isSheet ? 'stretch' : 'center',
        justifyContent: isSheet ? 'flex-end' : 'center',
      }}
    >
      <div
        className="absolute inset-0 bg-[var(--bg-overlay)] backdrop-blur-sm animate-overlay-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={clsx(
          'relative flex flex-col overflow-hidden bg-[var(--bg-raised)] border border-[var(--border-strong)] shadow-[var(--shadow-lg)]',
          isSheet
            ? 'h-full rounded-l-[var(--radius-xl)] animate-sheet-enter'
            : 'rounded-[var(--radius-xl)] mx-4 max-h-[90vh] animate-modal-enter',
        )}
        style={{ width: '100%', maxWidth: w }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || subtitle) && (
          <header className="flex items-start justify-between gap-4 px-6 py-5 border-b border-[var(--border)]">
            <div className="min-w-0">
              {title && (
                <h2 className="text-[18px] font-semibold text-[var(--text)] tracking-tight">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-1 text-[13px] text-[var(--text-muted)]">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть"
              className="shrink-0 -mr-1.5 -mt-1.5 inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text)] motion-base"
            >
              <X size={16} />
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        {footer && (
          <footer className="px-6 py-4 border-t border-[var(--border)] bg-[var(--bg-raised)]">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* ---------------------------------------------------------------------------
 * EmptyState
 * -------------------------------------------------------------------------*/
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-14">
      {icon && (
        <div className="w-14 h-14 rounded-full bg-[var(--accent-soft)] grid place-items-center text-[var(--accent-bright)] mb-4">
          {icon}
        </div>
      )}
      <h3 className="text-base font-semibold text-[var(--text)]">{title}</h3>
      {description && (
        <p className="text-sm text-[var(--text-muted)] mt-1.5 max-w-sm">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * StatCard — horizontal layout w/ delta (admin parity)
 * -------------------------------------------------------------------------*/
export function StatCard({
  label,
  value,
  sub,
  accent,
  icon,
  delta,
  trend,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: boolean;
  icon?: ReactNode;
  /** Signed percentage delta vs previous period. */
  delta?: number | null;
  /** @deprecated use `delta`. Kept for back-compat with V2 in-flight code. */
  trend?: 'up' | 'down' | 'flat';
}) {
  const up = typeof delta === 'number' && delta > 0;
  const down = typeof delta === 'number' && delta < 0;
  return (
    <div
      className={clsx(
        'group relative overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-raised)] p-5 shadow-[var(--shadow-sm)]',
        'transition-[border,box-shadow,transform] duration-[var(--duration-base)] ease-[var(--ease-out)]',
        'hover:border-[color:var(--border-strong)] hover:shadow-[var(--shadow-md),_0_0_0_1px_var(--accent-soft)]',
      )}
    >
      <div className="flex items-center gap-4">
        {icon && (
          <span
            className={clsx(
              'shrink-0 flex items-center justify-center opacity-90 transition-opacity group-hover:opacity-100',
              accent ? 'text-[var(--accent-bright)]' : 'text-[var(--text-muted)]',
            )}
            aria-hidden="true"
          >
            {icon}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)] leading-tight">
            {label}
          </p>
          <p
            className={clsx(
              'mt-1.5 text-[28px] font-bold leading-[1.05] tabular-nums',
              accent ? 'text-[var(--accent-bright)]' : 'text-[var(--text)]',
            )}
            style={{ letterSpacing: '-0.02em' }}
          >
            {value}
          </p>
          {(typeof delta === 'number' || sub) && (
            <div className="mt-1.5 flex items-center gap-2 text-[12px] leading-none">
              {typeof delta === 'number' && (
                <span
                  className={clsx(
                    'inline-flex items-center gap-0.5 font-medium tabular-nums',
                    up && 'text-[var(--success)]',
                    down && 'text-[var(--danger)]',
                    !up && !down && 'text-[var(--text-muted)]',
                  )}
                >
                  {up && <ArrowUpRight size={13} />}
                  {down && <ArrowDownRight size={13} />}
                  {delta > 0 ? '+' : ''}
                  {delta.toFixed(1)}%
                </span>
              )}
              {sub && (
                <span
                  className={clsx(
                    trend === 'up' && 'text-[var(--success)]',
                    trend === 'down' && 'text-[var(--danger)]',
                    (!trend || trend === 'flat') && 'text-[var(--text-muted)]',
                  )}
                >
                  {sub}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Segmented — pill-style segmented control (admin parity port).
 * -------------------------------------------------------------------------*/
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'sm',
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={clsx(
        'inline-flex items-stretch gap-0.5 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-elevated)] p-0.5',
        size === 'sm' ? 'h-8 text-[12px]' : 'h-9 text-[13px]',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={clsx(
              'inline-flex items-center justify-center gap-1.5 px-3 rounded-[calc(var(--radius-sm)-2px)] font-medium transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
              active
                ? 'bg-[var(--bg-base)] text-[var(--accent-bright)] shadow-[var(--shadow-sm)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text)]',
            )}
          >
            <span>{opt.label}</span>
            {opt.hint && (
              <span className="text-[10px] tabular-nums text-[var(--text-quiet)]">{opt.hint}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * IconButton (table action)
 * -------------------------------------------------------------------------*/
export function IconButton({
  className,
  tone = 'neutral',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'neutral' | 'danger' | 'accent' }) {
  return (
    <button
      {...rest}
      className={clsx(
        'h-8 w-8 grid place-items-center rounded-[var(--radius-sm)] text-[var(--text-muted)] motion-base',
        'hover:bg-[var(--bg-elevated)]',
        tone === 'neutral' && 'hover:text-[var(--text)]',
        tone === 'accent' && 'hover:text-[var(--accent-bright)]',
        tone === 'danger' && 'hover:text-[var(--danger)] hover:bg-[var(--danger-soft)]',
        className,
      )}
    />
  );
}

/* ---------------------------------------------------------------------------
 * SectionTitle — inside cards (kept for back-compat; also used as divider)
 * -------------------------------------------------------------------------*/
export function SectionTitle({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
      <h3 className="text-[15px] font-semibold tracking-tight text-[var(--text)]">{children}</h3>
      {action && <div>{action}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Table primitives (admin parity)
 * -------------------------------------------------------------------------*/
export function Table({ className, ...rest }: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <table
      {...rest}
      className={clsx('w-full text-[13px] border-collapse tabular-nums', className)}
    />
  );
}

export function THead({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      {...rest}
      className={clsx(
        'sticky top-0 z-10 border-b border-[var(--border)]',
        'bg-[color-mix(in_oklch,var(--bg-raised)_92%,transparent)] backdrop-blur-md',
        className,
      )}
    />
  );
}

export function TBody({ className, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...rest} className={className} />;
}

export function TR({ className, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      {...rest}
      className={clsx(
        'border-b border-[var(--border-subtle)] last:border-0',
        'transition-colors duration-150 ease-out',
        'hover:bg-[color-mix(in_oklch,var(--accent-bright)_5%,transparent)]',
        className,
      )}
    />
  );
}

export function TH({ className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      {...rest}
      className={clsx(
        'px-4 h-9 text-left text-[10px] font-semibold uppercase tracking-[var(--tracking-eyebrow)] text-[var(--text-quiet)] whitespace-nowrap align-middle',
        className,
      )}
    />
  );
}

export function TD({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      {...rest}
      className={clsx(
        'px-4 py-3 text-[var(--text)] align-middle text-[13px]',
        className,
      )}
    />
  );
}

/* ---------------------------------------------------------------------------
 * AlertBanner
 * -------------------------------------------------------------------------*/
export function AlertBanner({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: 'success' | 'warning' | 'danger' | 'info';
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={clsx(
        'rounded-[var(--radius-md)] px-4 py-3 text-[13px] leading-relaxed',
        tone === 'success' && 'bg-[var(--success-soft)] text-[var(--success)]',
        tone === 'warning' && 'bg-[var(--warning-soft)] text-[var(--warning)]',
        tone === 'danger' && 'bg-[var(--danger-soft)] text-[var(--danger)]',
        tone === 'info' && 'bg-[var(--info-soft)] text-[var(--info)]',
        className,
      )}
    >
      {title && <div className="font-semibold mb-0.5 text-[var(--text)]">{title}</div>}
      {children && <div className="text-[var(--text-secondary)]">{children}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Skeleton
 * -------------------------------------------------------------------------*/
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={clsx(
        'rounded-[var(--radius-sm)] bg-[var(--bg-elevated)] relative overflow-hidden skeleton-shimmer',
        className,
      )}
    />
  );
}

/* ---------------------------------------------------------------------------
 * ChecklistItem — onboarding row
 * -------------------------------------------------------------------------*/
export function ChecklistItem({
  done,
  title,
  description,
  action,
}: {
  done: boolean;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 px-4 py-3 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)]',
      )}
    >
      <span
        className={clsx(
          'mt-0.5 shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full',
          done
            ? 'bg-[var(--success-soft)] text-[var(--success)]'
            : 'text-[var(--text-quiet)]',
        )}
        aria-hidden="true"
      >
        {done ? <Check size={13} strokeWidth={2.5} /> : <Circle size={15} strokeWidth={1.5} />}
      </span>
      <div className="flex-1 min-w-0">
        <div
          className={clsx(
            'text-[14px] font-medium',
            done ? 'text-[var(--text-muted)] line-through' : 'text-[var(--text)]',
          )}
        >
          {title}
        </div>
        {description && (
          <p className="mt-0.5 text-[12px] text-[var(--text-muted)]">{description}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * FunnelBar — horizontal funnel with dropoff chips
 * -------------------------------------------------------------------------*/
export function FunnelBar({
  steps,
}: {
  steps: Array<{ label: string; count: number; dropoffPct?: number }>;
}) {
  const max = Math.max(1, ...steps.map((s) => s.count));
  return (
    <div className="flex flex-col gap-1.5">
      {steps.map((s, i) => {
        const ratio = Math.max(0.02, s.count / max);
        const next = steps[i + 1];
        return (
          <div key={s.label + i} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-[12px]">
              <span className="text-[var(--text-secondary)] font-medium">{s.label}</span>
              <span className="tabular-nums text-[var(--text)] font-semibold">
                {(s.count ?? 0).toLocaleString('ru-RU')}
              </span>
            </div>
            <div className="h-2.5 w-full rounded-[var(--radius-pill)] bg-[var(--bg-elevated)] overflow-hidden">
              <div
                className="h-full rounded-[var(--radius-pill)] bg-[var(--accent)]"
                style={{ width: `${ratio * 100}%` }}
              />
            </div>
            {next && typeof s.dropoffPct === 'number' && (
              <div className="flex justify-end">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[var(--radius-xs)] bg-[var(--danger-soft)] text-[var(--danger)] text-[10px] font-medium tabular-nums">
                  −{s.dropoffPct.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * ProgressBar
 * -------------------------------------------------------------------------*/
export function ProgressBar({
  value,
  label,
  valueLabel,
  tone = 'accent',
}: {
  value: number;
  label?: string;
  valueLabel?: string;
  tone?: 'accent' | 'success' | 'warning';
}) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const fill =
    tone === 'success'
      ? 'bg-[var(--success)]'
      : tone === 'warning'
        ? 'bg-[var(--warning)]'
        : 'bg-[var(--accent)]';
  return (
    <div className="flex flex-col gap-1.5">
      {(label || valueLabel) && (
        <div className="flex items-center justify-between text-[12px]">
          {label && <span className="text-[var(--text-secondary)] font-medium">{label}</span>}
          {valueLabel && (
            <span className="tabular-nums text-[var(--text)] font-semibold">{valueLabel}</span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        className="h-2 w-full rounded-[var(--radius-pill)] bg-[var(--bg-elevated)] overflow-hidden"
      >
        <div
          className={clsx('h-full rounded-[var(--radius-pill)] transition-[width] duration-[var(--duration-base)]', fill)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * QRCode — uses qrserver.com external service (no npm dep)
 * -------------------------------------------------------------------------*/
export function QRCode({ value, size = 160 }: { value: string; size?: number }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    value,
  )}`;
  return (
    <div
      className="inline-flex items-center justify-center rounded-[var(--radius-md)] bg-white p-2 border border-[var(--border)]"
      style={{ width: size + 16, height: size + 16 }}
    >
      <img src={src} alt="QR code" width={size} height={size} />
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Tabs — segmented control
 * -------------------------------------------------------------------------*/
export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (id: T) => void;
  items: Array<{ id: T; label: string; icon?: ReactNode }>;
}) {
  return (
    <div
      role="tablist"
      className="inline-flex items-center gap-0.5 p-0.5 rounded-[var(--radius-md)] bg-[var(--bg-elevated)] border border-[var(--border)]"
    >
      {items.map((it) => {
        const active = it.id === value;
        return (
          <button
            type="button"
            key={it.id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.id)}
            className={clsx(
              'inline-flex items-center gap-1.5 h-7 px-3 text-[12px] font-medium rounded-[var(--radius-sm)] motion-base',
              active
                ? 'bg-[var(--bg-raised)] text-[var(--text)] shadow-[var(--shadow-sm)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text)]',
            )}
          >
            {it.icon}
            <span>{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * CodeBlock — mono pre with copy button
 * -------------------------------------------------------------------------*/
export function CodeBlock({ code, lang }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="relative rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-base)] overflow-hidden">
      {lang && (
        <div className="px-3 py-1.5 border-b border-[var(--border)] text-[10px] uppercase tracking-[0.14em] font-semibold text-[var(--text-muted)]">
          {lang}
        </div>
      )}
      <pre className="p-3 pr-12 overflow-x-auto text-[12px] leading-relaxed font-mono text-[var(--text-secondary)] whitespace-pre">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={copy}
        aria-label="Скопировать"
        className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--bg-raised)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--bg-elevated)] motion-base"
      >
        {copied ? <CheckCircle2 size={13} className="text-[var(--success)]" /> : <Copy size={13} />}
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * MiniSparkline — inline svg sparkline (no axes, no legend)
 *
 * Effects: stroke draw-in on mount/data change, soft area gradient under
 * the line, glowing endpoint dot. Each row staggers via the `delayMs` prop.
 * -------------------------------------------------------------------------*/
let _miniSparkUid = 0;
export function MiniSparkline({
  data,
  color,
  width = 80,
  height = 24,
  delayMs = 0,
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  delayMs?: number;
}) {
  if (!data.length) {
    return <svg width={width} height={height} aria-hidden="true" />;
  }
  const max = Math.max(1, ...data);
  const min = Math.min(0, ...data);
  const range = Math.max(1, max - min);
  const step = data.length > 1 ? width / (data.length - 1) : 0;
  const pts = data.map((v, i) => ({ x: i * step, y: height - ((v - min) / range) * height }));
  const linePts = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `M0,${height} L ${linePts.replace(/ /g, ' L ')} L ${(width - 0).toFixed(1)},${height} Z`;
  const last = pts[pts.length - 1];
  const stroke = color ?? 'var(--accent-bright)';
  // Unique id per render so multiple sparklines coexist with their own gradient.
  const uid = ++_miniSparkUid;
  const fillId = `mini-spark-fill-${uid}`;
  const animKey = `${data.length}-${data[data.length - 1]}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={0.28} />
          <stop offset="100%" stopColor={stroke} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path
        key={`area-${animKey}`}
        d={areaPath}
        fill={`url(#${fillId})`}
        style={{
          opacity: 0,
          animation: `mini-spark-area-in 0.6s var(--ease-out) ${delayMs + 100}ms forwards`,
        }}
      />
      <polyline
        key={`line-${animKey}`}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={linePts}
        style={{
          strokeDasharray: 800,
          strokeDashoffset: 800,
          animation: `mini-spark-draw 0.9s cubic-bezier(0.65, 0, 0.35, 1) ${delayMs}ms forwards`,
          filter: `drop-shadow(0 0 2px color-mix(in oklch, ${stroke} 50%, transparent))`,
        }}
      />
      <circle
        cx={last.x}
        cy={last.y}
        r={1.8}
        fill={stroke}
        style={{
          opacity: 0,
          filter: `drop-shadow(0 0 3px ${stroke})`,
          animation: `mini-spark-dot-in 0.3s var(--ease-out) ${delayMs + 850}ms forwards`,
        }}
      />
      <style>{`
        @keyframes mini-spark-draw  { to { stroke-dashoffset: 0; } }
        @keyframes mini-spark-area-in { to { opacity: 1; } }
        @keyframes mini-spark-dot-in {
          from { opacity: 0; transform: scale(0); transform-origin: ${last.x}px ${last.y}px; }
          to   { opacity: 1; transform: scale(1); transform-origin: ${last.x}px ${last.y}px; }
        }
      `}</style>
    </svg>
  );
}

/* ---------------------------------------------------------------------------
 * Tooltip — native title-based tooltip wrapper.
 * -------------------------------------------------------------------------*/
export function Tooltip({
  content,
  children,
}: {
  content: ReactNode;
  children: ReactNode;
}) {
  const text = typeof content === 'string' ? content : '';
  return (
    <span className="inline-flex" title={text}>
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * Pulse — animated dot indicating live data
 * -------------------------------------------------------------------------*/
export function Pulse({ active, color }: { active: boolean; color?: string }) {
  const c = color ?? 'var(--success)';
  return (
    <span className="relative inline-flex h-2 w-2" aria-hidden="true">
      {active && (
        <span
          className="absolute inset-0 rounded-full opacity-75"
          style={{ background: c, animation: 'partnerPulsePing 1.5s ease-out infinite' }}
        />
      )}
      <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: c }} />
      <style>{`@keyframes partnerPulsePing { 0% { transform: scale(1); opacity: 0.75 } 75%,100% { transform: scale(2.4); opacity: 0 } }`}</style>
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * CopyButton — small button copying value to clipboard with feedback.
 * -------------------------------------------------------------------------*/
export function CopyButton({
  value,
  label,
  size = 'sm',
}: {
  value: string;
  label?: string;
  size?: 'xs' | 'sm';
}) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label ? `${label}: скопировать` : 'Скопировать'}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text)] hover:bg-[var(--bg-raised)] motion-base',
        size === 'xs' ? 'h-7 px-2 text-[11px]' : 'h-8 px-2.5 text-[12px]',
      )}
    >
      {copied ? (
        <CheckCircle2 size={12} className="text-[var(--success)]" />
      ) : (
        <Copy size={12} />
      )}
      {label && <span>{copied ? 'Скопировано' : label}</span>}
    </button>
  );
}

/* ---------------------------------------------------------------------------
 * HeatmapCalendar — GitHub-style activity grid.
 * cells must be ordered chronologically; rendered as columns of 7 days.
 * -------------------------------------------------------------------------*/
export function HeatmapCalendar({
  cells,
  maxValue,
  weeks: _weeks,
}: {
  cells: Array<{ date: string; value: number }>;
  maxValue: number;
  weeks?: number;
}) {
  void _weeks;
  if (!cells.length) {
    return (
      <div className="text-[12px] text-[var(--text-muted)]">Нет данных для отображения.</div>
    );
  }
  // group into weeks (cols of 7).
  const cols: Array<Array<{ date: string; value: number }>> = [];
  for (let i = 0; i < cells.length; i += 7) {
    cols.push(cells.slice(i, i + 7));
  }
  const intensity = (v: number): string => {
    if (v <= 0 || maxValue <= 0) return 'var(--bg-elevated)';
    const r = Math.min(1, v / maxValue);
    if (r < 0.25) return 'oklch(0.62 0.17 280 / 0.25)';
    if (r < 0.5) return 'oklch(0.62 0.17 280 / 0.5)';
    if (r < 0.75) return 'oklch(0.62 0.17 280 / 0.75)';
    return 'var(--accent-bright)';
  };
  return (
    <div className="flex gap-[3px] overflow-x-auto">
      {cols.map((col, ci) => (
        <div key={ci} className="flex flex-col gap-[3px]">
          {col.map((c) => (
            <div
              key={c.date}
              title={`${c.date}: ${c.value}`}
              className="h-2.5 w-2.5 rounded-[2px]"
              style={{ background: intensity(c.value) }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
